<template>
  <Dialog
    v-model:visible="dialogVisible"
    modal
    header="Verificación de Factura"
    :style="{ width: '550px', maxWidth: '95vw' }"
    @update:visible="handleClose"
  >
    <!-- Loading State -->
    <div v-if="loading" class="verify-container loading-state">
      <div class="loading-spinner">
        <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" />
        <p>Verificando factura...</p>
      </div>
    </div>

    <!-- Content -->
    <div v-else class="verify-container">
      <!-- Estado Principal -->
      <div :class="['status-badge', getStatusClass()]">
        <i :class="getStatusIcon()" />
        <span>{{
          datos.detalles?.siatResponse?.codigoDescripcion || datos.estado || "—"
        }}</span>
      </div>

      <!-- Grid compacto con toda la info -->
      <div class="compact-grid">
        <div class="info-row">
          <label>Factura N°</label>
          <span class="value-highlight">{{
            datos.detalles?.invoice?.number || "—"
          }}</span>
        </div>

        <div class="info-row">
          <label>Monto Total</label>
          <span class="value-amount">
            {{ formatCurrency(datos.detalles?.invoice?.total) }}
          </span>
        </div>

        <div class="info-row">
          <label>Fecha Emisión</label>
          <span>{{ formatDate(datos.detalles?.invoice?.createdAt) }}</span>
        </div>

        <div class="info-row">
          <label>Venta ID</label>
          <span>{{ datos.venta_id || "—" }}</span>
        </div>

        <div class="info-row">
          <label>Código Estado</label>
          <span class="code-small">{{
            datos.detalles?.siatResponse?.codigoEstado || "—"
          }}</span>
        </div>

        <div class="info-row">
          <label>Transacción</label>
          <span
            :class="[
              'status-inline',
              datos.detalles?.siatResponse?.transaccion ? 'success' : 'error',
            ]"
          >
            <i
              :class="
                datos.detalles?.siatResponse?.transaccion
                  ? 'pi pi-check-circle'
                  : 'pi pi-times-circle'
              "
            />
            {{
              datos.detalles?.siatResponse?.transaccion ? "Exitosa" : "Fallida"
            }}
          </span>
        </div>

        <!-- CUF -->
        <div class="info-row full-width">
          <label>CUF</label>
          <div class="code-inline">
            <span>{{ datos.cuf || "—" }}</span>
            <i
              v-if="datos.cuf"
              class="pi pi-copy"
              @click="copiar(datos.cuf, 'CUF')"
              v-tooltip.top="'Copiar'"
            />
          </div>
        </div>

        <!-- Código Recepción -->
        <div class="info-row full-width">
          <label>Código Recepción</label>
          <div class="code-inline">
            <span>{{
              datos.detalles?.siatResponse?.codigoRecepcion || "—"
            }}</span>
            <i
              v-if="datos.detalles?.siatResponse?.codigoRecepcion"
              class="pi pi-copy"
              @click="
                copiar(
                  datos.detalles.siatResponse.codigoRecepcion,
                  'Código Recepción'
                )
              "
              v-tooltip.top="'Copiar'"
            />
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <Button
        label="Cerrar"
        icon="pi pi-times"
        @click="handleClose"
        :disabled="loading"
        class="p-button-text"
      />
    </template>
  </Dialog>
</template>

<script>
export default {
  name: "FacturaEstadoVerificacion",
  props: {
    visible: { type: Boolean, required: true },
    datos: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
  },
  emits: ["update:visible", "closed"],
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      },
    },
  },
  methods: {
    handleClose() {
      this.$emit("update:visible", false);
      this.$emit("closed");
    },
    getStatusClass() {
      const estado = (
        this.datos.detalles?.siatResponse?.codigoDescripcion ||
        this.datos.estado ||
        ""
      ).toUpperCase();
      if (estado.includes("VALIDA")) return "valid";
      if (estado.includes("ANULADA")) return "cancelled";
      if (estado.includes("PENDIENTE")) return "pending";
      return "unknown";
    },
    getStatusIcon() {
      const estado = (
        this.datos.detalles?.siatResponse?.codigoDescripcion ||
        this.datos.estado ||
        ""
      ).toUpperCase();
      if (estado.includes("VALIDA")) return "pi pi-check-circle";
      if (estado.includes("ANULADA")) return "pi pi-times-circle";
      if (estado.includes("PENDIENTE")) return "pi pi-clock";
      return "pi pi-question-circle";
    },
    formatCurrency(value) {
      if (!value) return "—";
      const num = parseFloat(value);
      return new Intl.NumberFormat("es-BO", {
        style: "currency",
        currency: "BOB",
      }).format(num);
    },
    formatDate(dateString) {
      if (!dateString) return "—";
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("es-BO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    },
    copiar(texto, label) {
      navigator.clipboard.writeText(texto);
      this.$toast.add({
        severity: "success",
        summary: "Copiado",
        detail: `${label} copiado`,
        life: 2000,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.verify-container {
  padding: 0.75rem;

  &.loading-state {
    min-height: 180px;
  }
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem;

  p {
    margin: 0;
    color: #6b7280;
    font-size: 0.9375rem;
  }
}

.status-badge {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-weight: 600;
  font-size: 0.9375rem;

  i {
    font-size: 1.125rem;
  }

  &.valid {
    background: #10b981;
    color: white;
  }

  &.cancelled {
    background: #ef4444;
    color: white;
  }

  &.pending {
    background: #f59e0b;
    color: white;
  }

  &.unknown {
    background: #6b7280;
    color: white;
  }
}

.compact-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.625rem;
  background: #f9fafb;
  padding: 0.875rem;
  border-radius: 6px;
}

.info-row {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem;
  background: white;
  border-radius: 4px;
  border: 1px solid #e5e7eb;

  &.full-width {
    grid-column: 1 / -1;
  }

  label {
    font-size: 0.6875rem;
    color: #6b7280;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.3px;
  }

  span {
    font-size: 0.875rem;
    color: #111827;
    font-weight: 500;

    &.value-highlight {
      color: #6366f1;
      font-weight: 700;
      font-size: 1rem;
    }

    &.value-amount {
      color: #059669;
      font-weight: 700;
      font-size: 1rem;
    }

    &.code-small {
      display: inline-block;
      padding: 0.125rem 0.5rem;
      background: #6366f1;
      color: white;
      border-radius: 3px;
      font-family: monospace;
      font-size: 0.8125rem;
      font-weight: 600;
    }

    &.status-inline {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      font-weight: 600;

      &.success {
        color: #10b981;
      }

      &.error {
        color: #ef4444;
      }

      i {
        font-size: 0.875rem;
      }
    }
  }

  .code-inline {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.5rem;
    background: #f3f4f6;
    border-radius: 4px;
    font-family: monospace;
    font-size: 0.75rem;
    word-break: break-all;

    span {
      flex: 1;
      color: #374151;
      font-size: 0.75rem;
    }

    i {
      cursor: pointer;
      color: #6b7280;
      font-size: 0.875rem;
      transition: color 0.2s;
      flex-shrink: 0;

      &:hover {
        color: #6366f1;
      }
    }
  }
}

@media (max-width: 768px) {
  .compact-grid {
    grid-template-columns: 1fr;
  }

  .info-row.full-width {
    grid-column: 1;
  }
}
</style>
